import { Alert, Box, Button, Stack } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { EntityOpenModeSwitcher, SidePanelTabs } from 'src/components';
import { BlockContainer, BlockHeader } from 'src/components/Layout/named-layout-block.view';
import { PersonalizedSections } from 'src/components/SidePanelPersonalization';
import { PersonalizeSidePanelButton } from 'src/components/SidePanelPersonalization/personalize-side-panel-button';
import { resolvePartnerTabContactInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-contact-info';
import { PartnerInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-info';
import { PartnerInfoTabs } from 'src/components/SidePanelTabs/PartnerTab/partner-info-tabs';
import { SiteRelatedTabs } from 'src/components/SidePanelTabs/SiteTab/site-related-tabs';
import { SiteTab as SiteTabComponent } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SiteMenu } from 'src/components/SiteComponents/site-menu';
import { SiteShareButton } from 'src/components/SiteComponents/site-share-button';
import { Address, useGroupRules, useRestoreAddress, UserGroupRules } from 'src/graphql';
import { CustomAttributesSidePanelProvider } from 'src/graphql/entities/custom-attributes/side-panel-provider';
import { SidePanelPersonalizationProvider } from 'src/graphql/entities/side-panel-personalization';
import { CrossPartnerUserSettingsProvider } from 'src/graphql/entities/side-panel-personalization/cross-partner-user-settings-provider';
import { RecordOpenMode } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { EntityNames } from 'src/graphql/types';
import { PartnerHeader } from 'src/pages/Conversations/SidePanel/partner-header';
import { SiteCardFormProvider } from 'src/pages/Sites/SidePanel/site-card-form-provider';
import { siteSidePanelSurface } from 'src/pages/Sites/SidePanel/site-surface';
import { useRegisterConversationModalDefaults } from 'src/pages/Sites/use-register-conversation-modal-defaults';
import { useSiteQuickActions } from 'src/pages/Sites/use-site-quick-actions';
import { useGlobalStore } from 'src/store';
import { useCustomView } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

const CLOSE_DETAILS_PANEL_TOOLTIP = 'Close Site Details';

enum SiteDetailsTabs {
  Site = 'site',
  Partner = 'partner',
}

interface TabContentProps {
  site: Address;
  activeTab: SiteDetailsTabs;
  extendedView?: boolean;
}

interface SiteSidePanelProps {
  site: Address;
  onClose?: () => void;
  extendedView?: boolean;
}

export const SiteTab = ({
  address,
  extendedView,
}: {
  address: Address;
  extendedView?: boolean;
}) => {
  const { isMobile } = useScreenDimensions();
  const isDeleted = !!address?.isDeleted;

  const { checkValidity } = useGroupRules();
  const { restoreAddress } = useRestoreAddress();

  const onRestoreSiteClick = useCallback(async () => {
    await restoreAddress(address.addressId);
  }, [restoreAddress, address.addressId]);

  return (
    <SiteTabComponent
      address={address}
      disabled={isDeleted}
      propertiesAsRows
      addressRightAdornment={
        <>
          <PersonalizeSidePanelButton />
          <SiteShareButton
            address={address}
            partnerId={address?.partner?.partnerId ?? address?.partnerId}
          />
          {isMobile ? <SiteMenu address={address} /> : null}
        </>
      }
    >
      {isDeleted ? (
        <Alert
          severity="info"
          action={
            checkValidity([UserGroupRules.AccessToRestoreDeletedAddresses]) ? (
              <Button color="inherit" size="small" onClick={onRestoreSiteClick}>
                RESTORE
              </Button>
            ) : null
          }
        >
          This site was deleted
        </Alert>
      ) : null}

      {/* attribute rows continue the card's property rhythm — cancel the wrapper
          Stack's spacing step (same treatment as the labels Box in SiteTab) */}
      <Box sx={{ marginTop: (theme) => theme.utils.important(0) }}>
        <PersonalizedSections />
      </Box>

      {!extendedView && <SiteRelatedTabs address={address} variant="standard" />}
    </SiteTabComponent>
  );
};

const TabContent = ({ activeTab, site, extendedView }: TabContentProps) => {
  const partnerWithBrandGroupContactInfo = useMemo(
    () => resolvePartnerTabContactInfo(site.partner, site.brand?.brandGroupId),
    [site.brand?.brandGroupId, site.partner],
  );

  if (activeTab === SiteDetailsTabs.Site) {
    return <SiteTab address={site} extendedView={extendedView} />;
  }

  if (activeTab === SiteDetailsTabs.Partner) {
    return (
      <Stack spacing={2}>
        <PartnerInfo partner={partnerWithBrandGroupContactInfo} />
        {partnerWithBrandGroupContactInfo ? (
          <PartnerInfoTabs partner={partnerWithBrandGroupContactInfo} address={site} />
        ) : null}
      </Stack>
    );
  }

  return null;
};

export const SiteSidePanel = ({ onClose, extendedView, site }: SiteSidePanelProps) => {
  const { navigate } = useNavigateRoute();
  const { isMobile } = useScreenDimensions();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const [activeTab, setActiveTab] = useState(SiteDetailsTabs.Site);
  const { groupRules } = useGroupRules();
  const canAccessToPartnerTab = !!groupRules[UserGroupRules.AccessToPartnerTab];
  const canSeePartnersTab =
    canAccessToPartnerTab || (isMultiTenancyMode && selectedPartnerId === null);

  const customViewId = useCustomView(EntityNames.Site)?.customViewId;

  useSiteQuickActions(site);

  useRegisterConversationModalDefaults(site);

  const tabs = useMemo(
    () =>
      [
        {
          value: SiteDetailsTabs.Site,
          label: 'Site',
        },
        {
          value: SiteDetailsTabs.Partner,
          label: 'Partner',
          skip: !canSeePartnersTab,
          disabled: !site?.partner,
        },
      ].filter((tab) => !tab.skip),
    [canSeePartnersTab, site?.partner],
  );

  return (
    <CrossPartnerUserSettingsProvider>
      <CustomAttributesSidePanelProvider
        entity="Address"
        entityId={site.addressId}
        partnerId={site.partner?.partnerId ?? site.partnerId}
        canEdit={!site.isDeleted}
      >
        <SiteCardFormProvider address={site} disabled={!!site.isDeleted}>
          <SidePanelPersonalizationProvider surface={siteSidePanelSurface}>
            <BlockContainer
              sx={{
                height: '100%',
                overflow: 'auto',
              }}
            >
              <Stack direction="column" width="100%" flexGrow={1} minHeight={0}>
                {!extendedView && !isMobile && (
                  <BlockHeader sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                    <EntityOpenModeSwitcher
                      openMode={RecordOpenMode.FullPage}
                      onClick={() => {
                        navigate(portalPaths.site.full(site.id), { state: { customViewId } });
                      }}
                    />
                    <SiteMenu address={site} />
                  </BlockHeader>
                )}
                <PartnerHeader
                  displayPartnerInfo={canAccessToPartnerTab}
                  partner={site?.partner}
                  closeDetailsPanelTooltip={CLOSE_DETAILS_PANEL_TOOLTIP}
                  onClose={onClose}
                />
                <SidePanelTabs
                  key={site?.addressId}
                  sx={{
                    marginTop: (theme) => theme.utils.important(0),
                  }}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  tabs={tabs}
                  showLabel
                  hasBorderTop={false}
                >
                  <TabContent activeTab={activeTab} site={site} extendedView={extendedView} />
                </SidePanelTabs>
              </Stack>
            </BlockContainer>
          </SidePanelPersonalizationProvider>
        </SiteCardFormProvider>
      </CustomAttributesSidePanelProvider>
    </CrossPartnerUserSettingsProvider>
  );
};
